import { useTranslation } from 'next-i18next'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { SvdProductCard } from '@src/components/svd-product-card'
import { isPlSvdCard, mapPlSvdCard, type PlSvdCard } from '@src/components/svd-product-card/map-pl-svd-card'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { useSwipeableProductCardExperiment } from '@src/experiments/statsig-integration/use-swipeable-product-card-experiment.hook'
import { SelectedFilter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import NoProduct from '@src/pages/[...slug]/components/collection/molecules/noProducts/NoProduct'
import type { SearchProductCard } from '@src/pages/search/searchResultsSection/utils'
import { SkeletonPlaceholders } from '@src/quince-components/src/atoms/skeletonPlaceholders/SkeletonPlaceholders'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'

import { getTrackingMetaData, PRODUCT_CARD_OPTION_LIMIT, PRODUCT_VIEW_TRACKER } from './searchResults.constants'
import styles from './searchResults.module.scss'

// Identical for every search tile — matches search-results-content-ds.tsx's module-scope handler.
const handleSearchProductClick = () => {
  setFindingMethod[FINDING_METHOD_AGGREGATE.SEARCH]?.()
}

export interface SearchResultsContentLegacyProps {
  productCards: SearchProductCard[]
  showLoader: boolean
  maxWidth: number
  productViewTracker?: string
  searchQuery: string
  onResetFilters: () => void
  selectedFilters: SelectedFilter
}

export const SearchResultsContentLegacy = ({
  productCards,
  showLoader,
  maxWidth,
  searchQuery,
  onResetFilters,
  selectedFilters,
}: SearchResultsContentLegacyProps) => {
  const { t } = useTranslation()
  const hasAppliedFilters = SelectedFilter.isAnyFilterSelected(selectedFilters)
  const enableImageCarousel = useSwipeableProductCardExperiment()
  // TEMPORARY (STRFRNT-15810/STRFRNT-16013) — see `SVD_ENRICH_VERSION_HEADER` doc. Read once per grid,
  // not per card, so N tiles don't produce N Statsig exposures. The card shape guard below
  // (`isPlSvdCard`) only ever returns true when PL actually sent SVD-shaped data, which is exactly
  // what `useSearchResults.ts`'s fetch-header decision (same flag) controls.
  const isSvdEnabled = useIsSvdEnabled()

  if (showLoader) {
    return (
      <div className={styles.searchResultsWrapper}>
        <SkeletonPlaceholders length={10} />
      </div>
    )
  }

  if (productCards.length > 0) {
    return (
      <div className={`${styles.searchResultsWrapper} ${styles.searchResultsWrapper__newCLPFilters ?? ''}`}>
        {productCards.map((productCard, pIdx) => {
          const { product } = productCard
          const productClickTrackConfig = {
            trackInternal: true,
            category: 'search_results_collection',
            action: 'search_by_query',
            context: {
              searchQuery: searchQuery,
              position: pIdx,
              product_position: pIdx,
              ...getTrackingMetaData().context,
            },
          }

          // TEMPORARY (STRFRNT-15810/STRFRNT-16013 · search SVD preview): `isPlSvdCard` shape-guards
          // on anchor data, so a legacy-shaped card (enrich header off, or PL hasn't enriched this
          // hit) always falls through to the legacy `ProductCard` below. This check must run BEFORE
          // `getInitialSelectedVariant` — `displayConfig` is null at runtime for no-VDA SVD products,
          // and `initialSelectedVariant?.displayConfig.value` below would throw on that shape.
          if (isSvdEnabled && isPlSvdCard(product)) {
            const svdProduct = mapPlSvdCard(product as unknown as PlSvdCard)
            return (
              <SvdProductCard
                key={`${productCard.product.productId}_${pIdx}`}
                className={styles.productCard}
                product={svdProduct}
                maxWidth={maxWidth}
                enableImageCarousel={enableImageCarousel}
                productViewTracker={PRODUCT_VIEW_TRACKER}
                // Baked in per-card at fetch time (`transformProductsToProductCards`) — NOT the
                // sitewide "latest" queryId, which pagination overwrites. Reading the global value
                // here would pair an old card's objectID with a newer search's queryId once
                // load-more has run (review finding).
                searchAlgoliaQueryId={productCard.searchAlgoliaQueryId ?? ''}
                productSearchObjectId={svdProduct.searchObjectId ?? undefined}
                productPosition={pIdx + 1}
                searchQuery={searchQuery}
                isPageEligibleForHolidayTag
                onClickCb={handleSearchProductClick}
                productClickTrackConfig={productClickTrackConfig}
                // SAND-6848: legacy `/products/enrich` tags are scoped per color name, not
                // per-variant/SKU, and disagree with the SVD wire's own tags — skip enrich for
                // SVD-bucketed traffic. Keyed on `isSvdEnabled` (also applied to the legacy-fallback
                // `ProductCard` branch below), not `isPlSvdCard`, because the tag-refresh fetch queue
                // is a page-global last-caller-wins batch (`ProductDynamicDataStateManager`) — a
                // straggler legacy-shaped tile on this branch alone would silently re-enable the
                // refresh for every SVD tile on the grid.
                skipTagRefresh={isSvdEnabled}
              />
            )
          }

          const initialSelectedVariant = getInitialSelectedVariant({
            cardVariants: product.cardVariants,
          })
          return (
            <ProductCard
              shouldShowMore
              optionLimit={PRODUCT_CARD_OPTION_LIMIT}
              maxWidth={maxWidth}
              key={`${product.productId}_${initialSelectedVariant?.displayConfig.value}_${pIdx}`}
              className={styles.productCard}
              productCard={productCard}
              tracker={PRODUCT_VIEW_TRACKER}
              productViewTracker={PRODUCT_VIEW_TRACKER}
              searchQuery={searchQuery}
              position={pIdx + 1}
              enableImageCarousel={enableImageCarousel}
              productClickTrackConfig={productClickTrackConfig}
              onClickCb={handleSearchProductClick}
              isPageEligibleForHolidayTag={true}
              skipTagRefresh={isSvdEnabled}
            />
          )
        })}
      </div>
    )
  }

  return (
    <>
      {!hasAppliedFilters && (
        <h1
          className={`${styles.searchResultsHeader__title} ${styles.searchResultsHeader__title__noResults}`}
        >{`"${searchQuery.toUpperCase()}"`}</h1>
      )}
      <div className={styles.noSearchResultsWrapper} data-testid={TEST_LOCATORS.NO_SEARCH_RESULTS}>
        {hasAppliedFilters ? (
          <NoProduct onResetFilters={onResetFilters} isFiltersApplied={hasAppliedFilters} />
        ) : (
          t('no_search_results')
        )}
      </div>
    </>
  )
}
